import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation } from "wouter";
import { 
  useGetChatSession, 
  getGetChatSessionQueryKey,
  sendChatMessage,
  useFinishChatSession,
  useStartChatSession,
} from "@workspace/api-client-react";
import type { ChatMessageInput } from "@workspace/api-client-react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Send, CheckCircle2, AlertCircle, RefreshCw, Mic, MicOff, Clock } from "lucide-react";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { cn } from "@/lib/utils";
import { useVisualViewport } from "@/hooks/use-visual-viewport";
import { ParticipantInfoGate } from "@/components/participant-info-gate";
import { ParticipantRatingWidget } from "@/components/participant-rating-widget";

const SESSION_STORAGE_KEY = "disc_session_token";

function LogoMark() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="16" cy="16" r="15" stroke="hsl(262 83% 58%)" strokeWidth="2" fill="none"/>
      <circle cx="16" cy="16" r="9" stroke="hsl(262 83% 58%)" strokeWidth="2" fill="none" opacity="0.5"/>
      <circle cx="16" cy="16" r="3.5" fill="hsl(262 83% 58%)"/>
      <path d="M16 1.5 A14.5 14.5 0 0 1 30.5 16" stroke="hsl(262 83% 58%)" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
}

export default function ChatPage() {
  const params = useParams();
  const [, navigate] = useLocation();
  const urlToken = params.assessmentToken as string | undefined;

  const [resolvedToken, setResolvedToken] = useState<string | null>(null);

  useEffect(() => {
    if (urlToken) {
      localStorage.setItem(SESSION_STORAGE_KEY, urlToken);
      setResolvedToken(urlToken);
    } else {
      const saved = localStorage.getItem(SESSION_STORAGE_KEY);
      if (saved) {
        navigate(`/chat/${saved}`, { replace: true });
      } else {
        setResolvedToken("");
      }
    }
  }, [urlToken, navigate]);

  const assessmentToken = resolvedToken ?? "";

  const queryClient = useQueryClient();
  
  const [inputValue, setInputValue] = useState("");
  const [optimisticMessage, setOptimisticMessage] = useState<string | null>(null);
  const [participantInfoDone, setParticipantInfoDone] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [isConcluding, setIsConcluding] = useState(false);

  const { isSupported: isVoiceSupported, isListening, interimTranscript, audioLevel, toggleListening } = useVoiceInput({
    onTranscript: (text) => {
      setInputValue((prev) => (prev ? prev + " " + text : text));
      setSendError(null);
    },
  });
  const [resumeBannerVisible, setResumeBannerVisible] = useState(true);
  const lastMessageRef = useRef<string>("");
  const retryActionRef = useRef<"start" | "send">("send");
  const autoRetryCountRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const startedBlocksRef = useRef<Set<number>>(new Set());
  const vp = useVisualViewport();

  const clearErrorState = useCallback(() => {
    setSendError(null);
  }, []);
  
  const { data: session, isLoading, isError, error } = useGetChatSession(assessmentToken, {
    query: { 
      enabled: !!assessmentToken,
      queryKey: getGetChatSessionQueryKey(assessmentToken),
      retry: false
    }
  });

  useEffect(() => {
    if (session?.status === "completed" || session?.status === "expired") {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }, [session?.status]);

  useEffect(() => {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (isError && status === 410) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }, [isError, error]);

  const startSessionMutation = useStartChatSession({
    mutation: {
      onSuccess: (data) => {
        clearErrorState();
        queryClient.setQueryData(getGetChatSessionQueryKey(assessmentToken), data);
      },
      onError: (err: unknown) => {
        clearErrorState();
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 410) {
          queryClient.setQueryData(
            getGetChatSessionQueryKey(assessmentToken),
            (old: unknown) => old ? { ...(old as object), status: "expired" } : { status: "expired" },
          );
          return;
        }
        retryActionRef.current = "start";
        setSendError("Não foi possível iniciar a conversa. Tente novamente.");
      },
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: ({ token, data, signal }: { token: string; data: ChatMessageInput; signal?: AbortSignal }) =>
      sendChatMessage(token, data, signal ? { signal } : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetChatSessionQueryKey(assessmentToken) });
    },
  });

  const finishSessionMutation = useFinishChatSession({
    mutation: {
      onSuccess: () => {
        // Use refetchQueries (not just invalidateQueries) so the session status
        // is fetched immediately after /finish returns, without waiting for the
        // next user interaction to trigger the stale-while-revalidate cycle.
        queryClient.refetchQueries({ queryKey: getGetChatSessionQueryKey(assessmentToken) });
      }
    }
  });


  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  useEffect(() => {
    scrollToBottom();
  }, [session?.messages, optimisticMessage, sendMessageMutation.isPending, startSessionMutation.isPending, isConcluding]);

  useEffect(() => {
    if (vp !== null) {
      scrollToBottom("instant");
      window.scrollTo(0, 0);
    }
  }, [vp?.height, vp?.offsetTop]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [inputValue]);

  useEffect(() => {
    if (
      optimisticMessage &&
      session?.messages?.some(
        (m) => m.role === "user" && m.content === optimisticMessage
      )
    ) {
      setOptimisticMessage(null);
    }
  }, [session?.messages, optimisticMessage]);

  const needsParticipantInfo = !participantInfoDone && (
    !session?.participantName ||
    !session?.participantEmail
  );

  useEffect(() => {
    if (!session) return;
    if (session.format === "multiple_choice") {
      navigate(`/mc/${assessmentToken}`, { replace: true });
      return;
    }
    if (session.status === "completed" || session.status === "expired") return;
    if (needsParticipantInfo) return;

    const currentBlockMsgs = session.messages.filter(
      (m) => m.blockNumber === session.currentBlock
    );

    if (
      currentBlockMsgs.length === 0 &&
      !startedBlocksRef.current.has(session.currentBlock)
    ) {
      startedBlocksRef.current.add(session.currentBlock);
      startSessionMutation.mutate({ assessmentToken });
    }
  }, [session, needsParticipantInfo, assessmentToken, startSessionMutation]);

  // ── Callbacks ─────────────────────────────────────────────────────────────
  // All useCallback hooks MUST be declared here, before any conditional
  // early-return, to comply with React's Rules of Hooks.

  const doSend = useCallback((message: string) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    lastMessageRef.current = message;
    retryActionRef.current = "send";
    clearErrorState();

    sendMessageMutation.mutate({
      token: assessmentToken,
      data: { content: message },
      signal: controller.signal,
    }, {
      onSuccess: (data) => {
        abortControllerRef.current = null;
        autoRetryCountRef.current = 0;
        clearErrorState();
        setInputValue("");
        if (data.sessionCompleted) {
          setIsConcluding(true);
          finishSessionMutation.mutate({ assessmentToken });
        }
      },
      onError: (err) => {
        const isAbort = err instanceof Error && err.name === "AbortError";
        if (isAbort) return;
        abortControllerRef.current = null;
        clearErrorState();
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 410) {
          queryClient.setQueryData(
            getGetChatSessionQueryKey(assessmentToken),
            (old: unknown) => old ? { ...(old as object), status: "expired" } : { status: "expired" },
          );
          return;
        }
        if (status === undefined || status >= 500) {
          if (autoRetryCountRef.current < 2) {
            autoRetryCountRef.current += 1;
            setTimeout(() => doSend(message), 0);
            return;
          }
        }
        autoRetryCountRef.current = 0;
        retryActionRef.current = "send";
        setSendError("Não consegui enviar sua resposta. Tente novamente.");
      }
    });
  }, [sendMessageMutation, assessmentToken, finishSessionMutation, clearErrorState]);

  const handleSend = useCallback((e?: React.FormEvent, retryMessage?: string) => {
    e?.preventDefault();
    const message = retryMessage ?? inputValue.trim();
    if (!message) return;
    if (sendMessageMutation.isPending && !retryMessage) return;

    if (!retryMessage) {
      autoRetryCountRef.current = 0;
      setInputValue("");
      setOptimisticMessage(message);
    }

    doSend(message);
  }, [inputValue, sendMessageMutation.isPending, doSend]);

  const handleRetry = useCallback(() => {
    autoRetryCountRef.current = 0;
    if (retryActionRef.current === "start") {
      clearErrorState();
      startSessionMutation.mutate({ assessmentToken });
    } else {
      doSend(lastMessageRef.current);
    }
  }, [clearErrorState, startSessionMutation, assessmentToken, doSend]);

  // ── Early returns (after all hooks) ───────────────────────────────────────

  if (resolvedToken === null) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-[100dvh] bg-background max-w-2xl mx-auto w-full">
        <header className="px-4 py-4 border-b bg-card sm:px-6">
          <Skeleton className="h-5 w-40" />
        </header>
        <div className="h-0.5 bg-muted" />
        <div className="flex-1 p-4 space-y-5 sm:p-6">
          <Skeleton className="h-16 w-3/4 rounded-2xl rounded-tl-sm" />
          <Skeleton className="h-16 w-3/4 rounded-2xl rounded-tr-sm ml-auto" />
          <Skeleton className="h-16 w-3/4 rounded-2xl rounded-tl-sm" />
        </div>
        <div className="p-3 border-t bg-card sm:p-4">
          <Skeleton className="h-12 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!assessmentToken || isError || !session) {
    return (
      <div className="flex items-center justify-center min-h-[100dvh] bg-background p-6">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-5">
            <AlertCircle className="h-7 w-7 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Sessão Indisponível</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            O link desta sessão é inválido ou já expirou. Por favor, solicite um novo link de acesso.
          </p>
        </div>
      </div>
    );
  }

  if (session.status === "completed") {
    return (
      <div className="flex items-center justify-center min-h-[100dvh] bg-background p-6">
        <div className="max-w-sm w-full text-center animate-in fade-in zoom-in duration-500">
          <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-xl font-semibold mb-3 text-foreground">Muito obrigado pela sua participação!</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            Suas respostas foram registradas com sucesso. Foi um prazer ter você aqui.
          </p>
          <div className="bg-muted/60 rounded-xl px-5 py-4 text-left">
            <p className="text-xs font-semibold text-foreground mb-1 uppercase tracking-wide">Próximos passos</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Seus resultados serão analisados pela equipe responsável. Em breve você receberá um retorno sobre as próximas etapas.
            </p>
          </div>
          <ParticipantRatingWidget assessmentToken={assessmentToken} alreadyRated={session.participantRating != null} />
        </div>
      </div>
    );
  }

  if (session.status === "expired") {
    return (
      <div className="flex items-center justify-center min-h-[100dvh] bg-background p-6">
        <div className="max-w-sm w-full text-center">
          <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-5">
            <Clock className="h-7 w-7 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Sessão Expirada</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            O tempo limite para esta avaliação foi atingido. Por favor, contate o administrador.
          </p>
        </div>
      </div>
    );
  }

  if (needsParticipantInfo) {
    return (
      <ParticipantInfoGate
        assessmentToken={assessmentToken}
        onComplete={() => setParticipantInfoDone(true)}
        existingName={session?.participantName ?? undefined}
        existingEmail={session?.participantEmail ?? undefined}
      />
    );
  }

  const isBotTyping = startSessionMutation.isPending || sendMessageMutation.isPending;
  const isFinishing = finishSessionMutation.isPending;

  const currentBlock = Math.min(session.currentBlock, session.totalBlocks);
  const progressPct = Math.round((currentBlock / session.totalBlocks) * 100);

  const containerStyle: React.CSSProperties = vp
    ? {
        position: "fixed",
        top: `${vp.offsetTop}px`,
        left: 0,
        right: 0,
        height: `${vp.height}px`,
      }
    : { minHeight: "100dvh" };

  return (
    <div
      className="flex flex-col bg-background max-w-2xl mx-auto w-full overflow-hidden"
      style={containerStyle}
    >
      <header className="px-4 py-3 border-b bg-card sticky top-0 z-10 flex items-center justify-between shrink-0 sm:px-6 sm:py-4">
        <div className="flex items-center gap-2.5">
          <LogoMark />
          <span className="font-semibold text-[15px] text-foreground tracking-tight">Avaliação</span>
        </div>
        <div className="flex flex-col items-end gap-0.5">
          <span className="text-xs font-semibold text-foreground">
            Bloco {currentBlock} de {session.totalBlocks}
          </span>
          <span className="text-[10px] text-muted-foreground">{progressPct}% concluído</span>
        </div>
      </header>

      <div className="h-1 bg-muted relative overflow-hidden shrink-0">
        <div
          className="absolute inset-y-0 left-0 bg-primary transition-all duration-700 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      <div
        className="flex-1 px-4 py-4 space-y-4 overflow-y-auto sm:px-5 sm:py-5"
        role="log"
        aria-live="polite"
        aria-label="Conversa"
      >
        {resumeBannerVisible && session.messages.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-primary/8 border border-primary/20 animate-in fade-in duration-300">
            <RefreshCw className="h-4 w-4 text-primary shrink-0" />
            <p className="text-sm text-primary flex-1 leading-snug">
              Retomando de onde você parou — Bloco {Math.min(session.currentBlock, session.totalBlocks)} de {session.totalBlocks}
            </p>
            <button
              type="button"
              onClick={() => setResumeBannerVisible(false)}
              className="text-primary/60 hover:text-primary text-lg leading-none ml-1 shrink-0"
              aria-label="Fechar aviso de retomada"
            >
              ×
            </button>
          </div>
        )}
        {session.messages.map((msg, idx) => {
          const isUser = msg.role === "user";
          return (
            <div 
              key={msg.id || idx} 
              className={cn(
                "flex w-full animate-in fade-in slide-in-from-bottom-2 duration-300",
                isUser ? "justify-end" : "justify-start"
              )}
            >
              <div 
                className={cn(
                  "max-w-[85%] px-4 py-3 rounded-2xl text-[15px] leading-relaxed sm:text-[14.5px] sm:max-w-[82%]",
                  isUser 
                    ? "bg-primary text-primary-foreground rounded-tr-sm" 
                    : "bg-white shadow-sm text-card-foreground rounded-tl-sm"
                )}
              >
                {msg.content}
              </div>
            </div>
          );
        })}
        {optimisticMessage && (
          <div className="flex w-full animate-in fade-in slide-in-from-bottom-2 duration-300 justify-end">
            <div className="max-w-[85%] px-4 py-3 rounded-2xl text-[15px] leading-relaxed sm:text-[14.5px] sm:max-w-[82%] bg-primary text-primary-foreground rounded-tr-sm">
              {optimisticMessage}
            </div>
          </div>
        )}
        {isBotTyping && (
          <div className="flex w-full justify-start animate-in fade-in">
            <div className="px-4 py-3 rounded-2xl bg-white shadow-sm rounded-tl-sm flex space-x-1.5 items-center h-[48px]">
              <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
        {isConcluding && (
          <div className="flex w-full justify-start animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="max-w-[85%] px-4 py-3 rounded-2xl text-[15px] leading-relaxed sm:text-[14.5px] sm:max-w-[82%] bg-white shadow-sm text-card-foreground rounded-tl-sm">
              Estamos concluindo a sua pesquisa, aguarde...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} className="h-2" />
      </div>

      <div className="px-3 py-3 bg-card border-t shrink-0 sm:px-4 sm:py-4 space-y-2">
        {sendError && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-destructive/10 border border-destructive/20">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
            <p className="text-sm text-destructive flex-1 leading-snug">{sendError}</p>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              className="h-9 px-3 rounded-lg text-sm shrink-0 min-h-[44px] min-w-[44px]"
              onClick={handleRetry}
              aria-label="Tentar enviar novamente"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Tentar novamente
            </Button>
          </div>
        )}
        <form onSubmit={handleSend} className="flex gap-2 items-end">
          <Textarea
            ref={inputRef}
            value={isListening && interimTranscript ? inputValue + (inputValue ? " " : "") + interimTranscript : inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (sendError) setSendError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend(e as unknown as React.FormEvent);
              }
            }}
            placeholder={isListening ? "Ouvindo..." : "Digite sua resposta..."}
            className="flex-1 min-h-[48px] sm:min-h-[44px] max-h-[120px] rounded-xl px-4 py-3 bg-white border-border focus-visible:border-primary focus-visible:ring-primary/15 text-[15px] sm:text-[14.5px] resize-none overflow-hidden leading-normal"
            disabled={isBotTyping || isFinishing}
            onFocus={() => {
              window.scrollTo(0, 0);
              setTimeout(() => {
                window.scrollTo(0, 0);
                scrollToBottom("instant");
              }, 150);
            }}
            autoComplete="off"
            aria-label="Sua resposta"
            rows={1}
          />
          {isVoiceSupported && (
            <>
              {isListening && (
                <div
                  className="flex items-end gap-[3px] h-12 px-2 shrink-0 sm:h-11"
                  aria-hidden="true"
                >
                  {[0.4, 0.7, 1.0, 0.7, 0.4].map((scale, i) => (
                    <span
                      key={i}
                      className="inline-block w-[3px] rounded-full bg-red-500 transition-all duration-75"
                      style={{
                        height: `${Math.max(4, Math.round(audioLevel * 28 * scale + 4))}px`,
                      }}
                    />
                  ))}
                </div>
              )}
              <Button
                type="button"
                size="icon"
                variant={isListening ? "default" : "outline"}
                className={cn(
                  "h-12 w-12 rounded-xl shrink-0 sm:h-11 sm:w-11",
                  isListening && "bg-red-500 hover:bg-red-600 border-red-500"
                )}
                disabled={isBotTyping || isFinishing}
                onClick={toggleListening}
                aria-label={isListening ? "Parar gravação" : "Gravar resposta por voz"}
              >
                {isListening ? (
                  <MicOff className="h-4 w-4" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </Button>
            </>
          )}
          <Button 
            type="submit" 
            size="icon" 
            className="h-12 w-12 rounded-xl shrink-0 sm:h-11 sm:w-11"
            disabled={!inputValue.trim() || isBotTyping || isFinishing}
            aria-label="Enviar resposta"
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
